/**
 * `useSearchQuery` — the Search surface's data hook (frontend.md §5 step 9).
 *
 * Wraps `@tanstack/query`'s `useInfiniteQuery` over the three search tools
 * (`search_hybrid` / `search_lexical` / `search_semantic`). Cursor pagination: each page's
 * `meta.nextCursor` feeds the next fetch; `<VirtualizedResultList>` pulls the next page on
 * scroll (frontend.md §1.5 — virtualize + paginate, never "all").
 *
 * The retrieval mode and the query/filters come from the URL (frontend.md §2.2) — the
 * surface passes them in; this hook does not own them. `rerank` defaults to the
 * capability-tier default (D-004) when the surface leaves it unset.
 *
 * Every error routes through `useErrorHandler` (frontend.md §4.2 — "a query with no error
 * handling fails CI lint").
 */

import { useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';
import { searchHybrid, searchLexical, searchSemantic, searchByMetadata } from '@/api';
import type { Filters, ResultItem, ResultList } from '@/types';
import type { ToolResult } from '@/api';
import { RENDER_DEFAULTS } from '@/config/renderDefaults';
import { useErrorHandler } from './useErrorHandler';

export type SearchMode = 'hybrid' | 'lexical' | 'semantic';

export interface SearchQueryParams {
  query: string;
  mode: SearchMode;
  filters: Filters;
  /** undefined = use the static render-default (rerank dropped with local ML 2026-05-17). */
  rerank?: boolean;
}

/** One fetched page — the items plus the cursor + degradation flags from `meta`. */
export interface SearchPage {
  items: ResultItem[];
  nextCursor: string | null;
  truncated: boolean;
  warnings: string[];
  totalEstimate: number | null;
}

function toPage(result: ToolResult<ResultList>): SearchPage {
  return {
    items: result.data.items,
    nextCursor: result.meta.nextCursor,
    truncated: result.meta.truncated,
    warnings: result.meta.warnings,
    totalEstimate: result.meta.totalEstimate,
  };
}

export interface UseSearchQueryResult {
  query: UseInfiniteQueryResult<{ pages: SearchPage[]; pageParams: unknown[] }, Error>;
  /** All items across fetched pages, flattened — what the virtualized list renders. */
  items: ResultItem[];
  /** Truncation / degradation surfacing — frontend.md §1.5, §4.2. */
  truncated: boolean;
  warnings: string[];
  totalEstimate: number | null;
}

export function useSearchQuery(params: SearchQueryParams): UseSearchQueryResult {
  const { handleError } = useErrorHandler();
  const limit = RENDER_DEFAULTS.defaultPageSize;
  const rerank = params.rerank ?? RENDER_DEFAULTS.searchRerankDefault;
  const hasText = params.query.trim().length > 0;
  // A non-empty Tags/Entities (or notebook/source/date) facet should drive the search even
  // with no query text — matching notes by their TAG/ENTITY metadata (not title/body). With
  // no text we run `search_by_metadata` (pure filter); with text we run the chosen mode.
  const f = params.filters;
  const hasFilters =
    (f.tagPaths?.length ?? 0) > 0 ||
    (f.entityIds?.length ?? 0) > 0 ||
    (f.notebookIds?.length ?? 0) > 0 ||
    (f.sources?.length ?? 0) > 0 ||
    f.dateFrom != null ||
    f.dateTo != null;
  const enabled = hasText || hasFilters;

  const query = useInfiniteQuery({
    queryKey: ['search', params.mode, params.query, params.filters, rerank, limit],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<SearchPage> => {
      const cursor = pageParam;
      try {
        if (!hasText) {
          // Facet-only search: filter notes by their metadata (tags/entities/…).
          return toPage(await searchByMetadata({ filters: params.filters, limit, cursor }));
        }
        if (params.mode === 'lexical') {
          return toPage(
            await searchLexical({ query: params.query, filters: params.filters, limit, cursor }),
          );
        }
        if (params.mode === 'semantic') {
          return toPage(
            await searchSemantic({ query: params.query, filters: params.filters, limit, cursor }),
          );
        }
        return toPage(
          await searchHybrid({
            query: params.query,
            filters: params.filters,
            rerank,
            limit,
            cursor,
          }),
        );
      } catch (err) {
        handleError(err, () => void query.refetch());
        throw err;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const pages = query.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items);
  const lastPage = pages[pages.length - 1];
  return {
    query,
    items,
    truncated: pages.some((p) => p.truncated),
    warnings: lastPage?.warnings ?? [],
    totalEstimate: lastPage?.totalEstimate ?? null,
  };
}
